// Fichier: src/pages/GoalForm.js
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { db } from '../firebase';
import { 
  Box, 
  Typography, 
  Button, 
  TextField, 
  Select, 
  MenuItem, 
  InputLabel, 
  FormControl, 
  Chip,
  Grid,
  FormControlLabel,
  Checkbox,
  CircularProgress,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { 
  collection, 
  getDocs, 
  doc, 
  getDoc, 
  setDoc,
  query,
  where,
  Timestamp
} from 'firebase/firestore';

const GoalForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [type, setType] = useState('daily');
  const [assignmentType, setAssignmentType] = useState('course');
  const [assignedTo, setAssignedTo] = useState([]);
  const [deadline, setDeadline] = useState('');
  const [students, setStudents] = useState([]);
  const [courses, setCourses] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [coursesWithStudents, setCoursesWithStudents] = useState({});

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // 1. Charger tous les cours depuis dance_courses (comme dans MyCoursesSection)
        const coursesSnapshot = await getDocs(collection(db, 'dance_courses'));
        const coursesData = coursesSnapshot.docs.map(doc => ({ 
          id: doc.id, 
          ...doc.data(),
          title: doc.data().title || 'Cours sans titre'
        }));
        setCourses(coursesData);
        
        // 2. Charger tous les utilisateurs (élèves)
        const usersSnapshot = await getDocs(collection(db, 'users'));
        const allUsers = usersSnapshot.docs.map(doc => ({ 
          id: doc.id, 
          ...doc.data()
        }));
        
        // 3. Créer une structure pour grouper les élèves par cours
        const coursesMap = {};
        
        // Initialiser chaque cours dans la map
        coursesData.forEach(course => {
          coursesMap[course.id] = {
            title: course.title,
            students: []
          };
        });
        
        // 4. Récupérer les étudiants inscrits aux cours depuis plusieurs sources
        // Source 1: dance_courses.enrolledStudents
        coursesData.forEach(course => {
          const enrolledStudents = course.enrolledStudents || [];
          enrolledStudents.forEach(studentId => {
            const student = allUsers.find(u => u.id === studentId);
            if (student && !coursesMap[course.id].students.some(s => s.id === studentId)) {
              coursesMap[course.id].students.push({
                id: studentId,
                firstName: student.firstName || '',
                lastName: student.lastName || '',
                email: student.email || '',
                fullName: `${student.firstName || ''} ${student.lastName || ''}`.trim() || student.email || studentId
              });
            }
          });
        });
        
        // Source 2: registrations collection (comme dans MyCoursesSection)
        const registrationsSnapshot = await getDocs(collection(db, 'registrations'));
        registrationsSnapshot.forEach(regDoc => {
          const registration = regDoc.data();
          const courseId = registration.courseId;
          
          if (courseId && coursesMap[courseId]) {
            // Ajouter chaque personne (élève) du formulaire d'inscription
            (registration.persons || []).forEach(personId => {
              const student = allUsers.find(u => u.id === personId);
              if (student && !coursesMap[courseId].students.some(s => s.id === personId)) {
                coursesMap[courseId].students.push({
                  id: personId,
                  firstName: student.firstName || '',
                  lastName: student.lastName || '',
                  email: student.email || '',
                  fullName: `${student.firstName || ''} ${student.lastName || ''}`.trim() || student.email || personId
                });
              }
            });
          }
        });
        
        // Source 3: users.courseIds (comme dans MyCoursesSection)
        allUsers.forEach(user => {
          const userCourseIds = user.courseIds || user.courses || [];
          userCourseIds.forEach(courseId => {
            if (coursesMap[courseId] && !coursesMap[courseId].students.some(s => s.id === user.id)) {
              coursesMap[courseId].students.push({
                id: user.id,
                firstName: user.firstName || '',
                lastName: user.lastName || '',
                email: user.email || '',
                fullName: `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email || user.id
              });
            }
          });
        });
        
        // 5. Trier les élèves dans chaque cours par nom complet
        Object.values(coursesMap).forEach(course => {
          course.students.sort((a, b) => a.fullName.localeCompare(b.fullName));
        });
        
        setCoursesWithStudents(coursesMap);

        // Si en mode édition, charger l'objectif existant
        if (id) {
          setIsEditing(true);
          const goalDoc = await getDoc(doc(db, 'objectives', id));
          if (goalDoc.exists()) {
            const goalData = goalDoc.data();
            setTitle(goalData.title);
            setDescription(goalData.description);
            setType(goalData.type);
            setAssignmentType(goalData.assignmentType);
            setAssignedTo(goalData.assignedTo || []);
            
            // Convertir Firestore Timestamp en format datetime-local
            if (goalData.deadline) {
              const date = goalData.deadline.toDate();
              const year = date.getFullYear();
              const month = String(date.getMonth() + 1).padStart(2, '0');
              const day = String(date.getDate()).padStart(2, '0');
              const hours = String(date.getHours()).padStart(2, '0');
              const minutes = String(date.getMinutes()).padStart(2, '0');
              setDeadline(`${year}-${month}-${day}T${hours}:${minutes}`);
            }
          }
        }
      } catch (error) {
        console.error("Erreur de chargement des données:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);

  const handleAssignmentToggle = (itemId) => {
    setAssignedTo(prev => 
      prev.includes(itemId) 
        ? prev.filter(id => id !== itemId) 
        : [...prev, itemId]
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Convertir la date en Timestamp Firestore
    let deadlineTimestamp = null;
    if (deadline) {
      deadlineTimestamp = Timestamp.fromDate(new Date(deadline));
    }

    const goalData = {
      title,
      description,
      type,
      assignmentType,
      assignedTo,
      deadline: deadlineTimestamp,
      createdAt: new Date(),
      updatedAt: new Date()
    };

    try {
      if (isEditing) {
        await setDoc(doc(db, 'objectives', id), goalData, { merge: true });
      } else {
        const newDocRef = doc(collection(db, 'objectives'));
        await setDoc(newDocRef, goalData);
      }
      navigate('/goals');
    } catch (error) {
      console.error("Erreur sauvegarde :", error);
    }
  };

  const renderAssignmentList = () => {
    if (loading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress size={60} sx={{ color: '#7C3AED' }} />
        </Box>
      );
    }

    if (assignmentType === 'course') {
      return (
        <Grid container spacing={2} sx={{ mt: 1 }}>
          {courses.map(course => (
            <Grid item xs={6} sm={4} md={3} key={course.id}>
              <FormControlLabel
                control={
                  <Checkbox 
                    checked={assignedTo.includes(course.id)}
                    onChange={() => handleAssignmentToggle(course.id)}
                  />
                }
                label={course.title}
              />
            </Grid>
          ))}
        </Grid>
      );
    } else {
      // Vérifier s'il y a des cours avec des élèves
      const hasStudents = Object.values(coursesWithStudents).some(
        course => course.students.length > 0
      );

      if (!hasStudents) {
        return (
          <Typography variant="body1" sx={{ p: 3, textAlign: 'center', color: 'text.secondary' }}>
            Aucun élève trouvé dans les cours. Veuillez d'abord inscrire des élèves.
          </Typography>
        );
      }

      return (
        <Box sx={{ mt: 2 }}>
          {Object.entries(coursesWithStudents)
            .filter(([_, courseData]) => courseData.students.length > 0)
            .map(([courseId, courseData]) => (
              <Accordion key={courseId} defaultExpanded sx={{ mb: 2, border: '1px solid #e0e0e0' }}>
                <AccordionSummary 
                  expandIcon={<ExpandMoreIcon />}
                  sx={{ backgroundColor: '#f5f5f5' }}
                >
                  <Typography sx={{ fontWeight: 'bold' }}>
                    {courseData.title} 
                    <Chip 
                      label={`${courseData.students.length} élèves`} 
                      size="small" 
                      sx={{ ml: 2, backgroundColor: '#7C3AED', color: 'white' }} 
                    />
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ backgroundColor: '#fafafa' }}>
                  <Grid container spacing={1}>
                    {courseData.students.map(student => (
                      <Grid item xs={12} sm={6} key={student.id}>
                        <FormControlLabel
                          control={
                            <Checkbox 
                              checked={assignedTo.includes(student.id)}
                              onChange={() => handleAssignmentToggle(student.id)}
                              color="primary"
                            />
                          }
                          label={
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <span>{student.fullName}</span>
                              {student.email && (
                                <Typography variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>
                                  ({student.email})
                                </Typography>
                              )}
                              {assignedTo.includes(student.id) && (
                                <Chip 
                                  label="Assigné" 
                                  size="small" 
                                  color="success" 
                                  sx={{ ml: 1 }} 
                                />
                              )}
                            </Box>
                          }
                          sx={{ 
                            width: '100%',
                            '&:hover': { backgroundColor: '#f0f0f0' }
                          }}
                        />
                      </Grid>
                    ))}
                  </Grid>
                </AccordionDetails>
              </Accordion>
            ))}
        </Box>
      );
    }
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1200, margin: '0 auto' }}>
      <Paper elevation={3} sx={{ p: 3, mb: 3, backgroundColor: '#ffffff' }}>
        <Typography variant="h5" sx={{ mb: 3, fontWeight: 'bold', color: '#7C3AED' }}>
          {isEditing ? 'Modifier Objectif' : 'Créer Nouvel Objectif'}
        </Typography>

        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                label="Titre"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                fullWidth
                required
                variant="outlined"
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <FormControl fullWidth variant="outlined">
                <InputLabel>Type d'objectif</InputLabel>
                <Select
                  value={type}
                  label="Type d'objectif"
                  onChange={(e) => setType(e.target.value)}
                >
                  <MenuItem value="daily">Journalier</MenuItem>
                  <MenuItem value="weekly">Hebdomadaire</MenuItem>
                  <MenuItem value="monthly">Mensuel</MenuItem>
                  <MenuItem value="yearly">Annuel</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                label="Description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                fullWidth
                multiline
                rows={4}
                required
                variant="outlined"
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <FormControl fullWidth variant="outlined">
                <InputLabel>Assignation</InputLabel>
                <Select
                  value={assignmentType}
                  label="Assignation"
                  onChange={(e) => setAssignmentType(e.target.value)}
                >
                  <MenuItem value="course">Par cours</MenuItem>
                  <MenuItem value="student">Par élève</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                label="Deadline (optionnelle)"
                type="datetime-local"
                value={deadline}
                onChange={(e) => setDeadline(e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
              <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                Date limite pour atteindre l'objectif
              </Typography>
            </Grid>
            
            <Grid item xs={12}>
              <Typography variant="subtitle1" sx={{ mt: 1, mb: 2, fontWeight: 'bold', color: '#333' }}>
                Assigné à:
              </Typography>
              {renderAssignmentList()}
            </Grid>
          </Grid>

          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
            <Button 
              variant="outlined" 
              component={Link} 
              to="/goals"
              sx={{ color: '#7C3AED', borderColor: '#7C3AED' }}
            >
              Annuler
            </Button>
            <Button 
              type="submit" 
              variant="contained" 
              sx={{ backgroundColor: '#7C3AED', '&:hover': { backgroundColor: '#6D28D9' } }}
            >
              {isEditing ? 'Mettre à jour' : 'Créer'}
            </Button>
          </Box>
        </form>
      </Paper>
    </Box>
  );
};

export default GoalForm;